<script setup lang="ts">
import FinestKpiCard from '@/components/FinestKpiCard.vue'
import { useI18n } from 'vue-i18n'

defineProps<{
  income: number
  expenses: number
  assets: number
  liabilities: number
  debtRatio: number | null
}>()

const { t, n } = useI18n({ useScope: 'local' })

const monthly = (yearly: number): number => yearly / 12
</script>

<template>
  <div class="flex flex-col gap-6">
    <h2 class="text-xl font-bold">{{ t('heading') }}</h2>

    <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <FinestKpiCard
        class="bg-surface-2! shadow-none!"
        :title="t('tiles.assets')"
        :value="assets"
        format="currency"
        :maximum-fraction-digits="0"
        tone="positive" />

      <FinestKpiCard
        class="bg-surface-2! shadow-none!"
        :title="t('tiles.liabilities')"
        :value="liabilities"
        format="currency"
        :maximum-fraction-digits="0" />

      <FinestKpiCard
        class="bg-surface-2! shadow-none!"
        :title="t('tiles.debtRatio')"
        :value="debtRatio ?? t('empty')"
        format="percent"
        :maximum-fraction-digits="2"
        :subtitle="t('debtRatioHint')" />

      <FinestKpiCard
        class="bg-surface-2! shadow-none!"
        :title="t('tiles.income')"
        :value="income"
        format="currency"
        :maximum-fraction-digits="0"
        tone="positive"
        :subtitle="t('perMonth', { amount: n(monthly(income), 'euros') })" />

      <FinestKpiCard
        class="bg-surface-2! shadow-none!"
        :title="t('tiles.expenses')"
        :value="expenses"
        format="currency"
        :maximum-fraction-digits="0"
        :subtitle="t('perMonth', { amount: n(monthly(expenses), 'euros') })" />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "heading": "Gesamtübersicht - Haushalt",
    "empty": "—",
    "perMonth": "{amount} / Monat",
    "debtRatioHint": "Verbindlichkeiten zum Vermögen",
    "tiles": {
      "assets": "Vermögen",
      "liabilities": "Verbindlichkeiten",
      "debtRatio": "Verschuldungsgrad",
      "income": "Einnahmen / Jahr",
      "expenses": "Ausgaben / Jahr"
    }
  },
  "en": {
    "heading": "Overall view - household",
    "empty": "—",
    "perMonth": "{amount} / month",
    "debtRatioHint": "Liabilities relative to assets",
    "tiles": {
      "assets": "Assets",
      "liabilities": "Liabilities",
      "debtRatio": "Debt ratio",
      "income": "Income / year",
      "expenses": "Expenses / year"
    }
  }
}
</i18n>
